class CartDrawerRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartDrawer = this.closest('cart-drawer');
      cartDrawer.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-drawer-remove-button', CartDrawerRemoveButton);

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.MOBILE_WIDTH_QUERY = '(max-width: 990px)';
    this.isCartPage = document.body.classList.contains('template--cart');

    this.drawer = document.getElementById('cart-drawer');
    this.drawerInner = this.drawer.querySelector('.cart-drawer__inner-wrapper');
    this.header = document.querySelector('sticky-header');
    this.cartDrawerOpen = document.querySelector(
      '[data-js-cart-drawer="cartDrawerOpen"]'
    );
    this.cartDrawerClose = document.querySelector(
      '[data-js-cart-drawer="cartDrawerClose"]'
    );

    if (!this.isCartPage) {
      this.onBodyClick = this.handleBodyClick.bind(this);
    }

    this.bodyScrollLock = window.bodyScrollLock || {};

    this.lineItemStatusElement = document.getElementById(
      'cart-drawer-line-item-status'
    );

    this.currentItemCount = Array.from(
      this.querySelectorAll('[name="updates[]"]')
    ).reduce(
      (total, quantityInput) => total + parseInt(quantityInput.value),
      0
    );

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.debouncedOnResize = debounce((event) => {
      this.onResize(event);
    }, 300);

    this.addEventListener('change', this.debouncedOnChange.bind(this));
    this.addEventListener('cartReRender', this.reRenderItems.bind(this), { once: true });

    if (this.isCartPage) {
      window.addEventListener('resize', this.debouncedOnResize.bind(this));
      window.addEventListener('load', this.onResize.bind(this));
    }

    this.drawer.addEventListener(
      'keyup',
      (event) => event.code === 'Escape' && this.close()
    );
    [...this.querySelectorAll('[data-js-cart-drawer="cartDrawerClose"]')].forEach(
      (closeButton) => {
        this.activeElement = document.activeElement || this.cartDrawerOpen;
        closeButton.addEventListener('click', this.close.bind(this));
      }
    );

    [
      ...document
      .querySelectorAll('[data-js-cart-drawer="cartDrawerOpen"]')
    ].forEach((openButton) => {
        openButton.addEventListener('click', this.handleDrawerOpen.bind(this));
      });
  }

  isIos() {
    return [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod'
    ].includes(navigator.platform)
    // iPad on iOS 13 detection
    || (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name')
    );
  }

  onResize(event) {
    const isMobile = window.matchMedia(this.MOBILE_WIDTH_QUERY).matches;
    const isCartClosed = !this.drawer.classList.contains('active');
    const cartShouldOpen = isMobile && isCartClosed;
    const cartShouldClose = !isMobile && !isCartClosed;
    const headerHeight = document.querySelector('.js-header').offsetHeight;

    if (cartShouldOpen) {
      this.open(true);
      this.drawer.style.cssText =`
        height: calc(100% - ${headerHeight}px);
        top: ${headerHeight}px;
      `
    }

    if (cartShouldClose) {
      this.close();
    }
  }

  /**
   * Re-render cart-drawer items
   */
  reRenderItems(response = false) {
    let promisesList = [];

    if (response) {
      setTimeout(() => {
        const promise = new Promise((resolve, reject) => {
          fetch('/?section_id=cart-drawer')
            .then(response => {
              return response.text();
            })
            .then(text => {
              if (text) {
                resolve(text);
                return;
              }

              reject(text);
            })
            .catch(error => {
              window.console.error(error);
            });
        });

        promisesList.push(promise);
        promisesList.push(response);

        this.handleReRender(promisesList);
      }, 300);
    }
  }

  /**
   * Open the cart-drawer
   */
  handleDrawerOpen() {
    this.open();
  }

  /**
   * Triggered whenever product-form was submitted and item was added to cart
   * @param  {Object} Added to cart item data
   */
  handleAddedItem(addedItemResponse) {
    const fetchedCartData = this.getCart();
    this.handleCartData(fetchedCartData, addedItemResponse);
  }

  /**
   * Get Cart data from Shopify Ajax API
   * @return {Object} Promise
   */
  getCart() {
    return new Promise((resolve, reject) => {
      fetch('/cart.js')
        .then((response) => response.json())
        .then((response) => {
          if (response) {
            resolve(response);
            return;
          }

          reject(response);
        })
        .catch((e) => {
          window.console.error(error);
        });
    });
  }

  /**
   * Handle cart data after the response from Shopify Ajax API
   * @param  {Object} Promise
   * @param  {Object} Added to cart item data
   */
  handleCartData(promise, addedItemResponse = false) {
    Promise.all([promise])
    .then((values) => {
        if (values.length === 0) {
          return;
        }

        const cartData = values[0];

        if (addedItemResponse !== false) {
          const line = cartData.items.map((item, index) => {
            if (item.id === addedItemResponse.id) {
              return index;
            }
          });

          this.renderContents(cartData, addedItemResponse, line, '', false);

          // Dispatch cartChange event for Free samples logic.
          window.dispatchEvent(new Event('cartChange', { bubbles: true }));
        }
      })
      .catch((error) => {
        window.console.error(error);
      });
  }

  /**
   * Handle re render of cart data
   * @param  {Object} Promise
   */
  handleReRender(promises) {
    Promise.all(promises)
    .then((values) => {
      if (values.length === 0) {
        return;
      }

      const cartDrawerHTML = values[0];
      const cartData = values[1];

      if (cartDrawerHTML.length) {
        this.renderContents(cartData, cartDrawerHTML, '', '', false);
      }
    })
    .catch((error) => {
      window.console.error(error);
    });
  }

  /**
   * Get sections to render
   * @return {Object}
   */
  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: document.getElementById('cart-drawer').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-drawer',
        section: document.getElementById('cart-drawer').dataset.id,
        selector: '.js-samples-popup-content',
      },
      {
        id: 'main-cart-items',
        section: 'main-cart-items',
        selector: '.js-cart-items-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        this.renderContents(parsedState, false, line, name, true);

        // Dispatch cartChange event for Free samples logic.
        window.dispatchEvent(
          new Event('cartChange', {bubbles: true})
        );

        if (quantity == 0) {
          this.updateTrappedFocus();
        }
      })
      .catch(() => {
        [...this.querySelectorAll('.loading-overlay')].forEach((overlay) =>
          overlay.classList.add('hidden')
        );
        document.getElementById('cart-drawer-errors').textContent =
          window.cartStrings.error;
        this.disableLoading();
      });
  }

  /**
   * Open cart drawer
   * @param {Boolean} noReload Flag used specifically to open drawer on cart page mobile
   * @returns
   */
  open(noReload) {
    if (this.isCartPage && !noReload) {
      // location.reload();
      return;
    }

    this.activeElement = document.activeElement || this.cartDrawerOpen;
    this.drawer.classList.add('animate', 'active');

    if (noReload) {
      this.drawer.classList.add('on-cart-page-mobile');
    }

    this.drawer.addEventListener(
      'transitionend',
      () => {
        this.drawer.focus();
        trapFocus(this.drawer, this.cartDrawerClose);
      },
      { once: true }
    );

    if (this.isIos()) {
      document.body.classList.add('body-no-scroll');
    } else {
      this.bodyScrollLock.disableBodyScroll(this.drawerInner);
    }

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    if (window.matchMedia(this.MOBILE_WIDTH_QUERY).matches && this.isCartPage) {
      return;
    }

    this.drawer.classList.remove('active');

    document.body.removeEventListener('click', this.onBodyClick);

    if (this.isIos()) {
      document.body.classList.remove('body-no-scroll');
    } else {
      this.bodyScrollLock.enableBodyScroll(this.drawerInner);
    }

    removeTrapFocus(this.activeElement);
  }

  /**
   * Render Contents
   * @param  {Object} Cart data
   * @param  {Object/Boolean} Added to cart item data / False
   * @param  {Number}  Line item index
   * @param  {String}  Line item input name
   * @param  {Boolean} Whether Cart-drawer is opened
   */
  renderContents(
    parsedState,
    addedItemResponse = false,
    line,
    name,
    isDrawerOpened = false
  ) {
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    const cartFooter = document.getElementById('cart-drawer-footer');

    const cartItemCounter = document.querySelector(
      '[js-cart-drawer="item-counter"]'
    );

    if (cartItemCounter) {
      const itemsCount = parsedState?.items
      ?.map(item => item.quantity)
      .reduce((acc, val) => {
        return acc + val;
      }, 0);


      if (itemsCount) {
        cartItemCounter.textContent = itemsCount;
        cartItemCounter.classList.add('active');
      } else {
        cartItemCounter.classList.remove('active');
      }
    }

    if (cartFooter)
      cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);

    let state = parsedState;

    if (addedItemResponse !== false && typeof addedItemResponse == 'object') {
      state = addedItemResponse;
    }

    this.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id).querySelector(section.selector) ||
        document.getElementById(section.id);

      if (state?.sections) {
        elementToReplace.innerHTML = this.getSectionInnerHTML(
          state.sections[section.section],
          section.selector
        );

        // Update cart page recommended items
        const cartRecommendations = document.querySelector(
          '.js-cart-recommendations'
        );

        if (
          cartRecommendations &&
          state.sections[section.section].indexOf('js-cart-recommendations') !== -1
        ) {
          const newState = this.getSectionInnerHTML(
            state.sections[section.section],
            '.js-cart-recommendations'
          );

          cartRecommendations.innerHTML = newState;
        }
      }

      if (addedItemResponse !== false && typeof addedItemResponse == 'string') {
        elementToReplace.innerHTML = this.getSectionInnerHTML(
          addedItemResponse,
          section.selector
        );
      }
    });

    this.updateLiveRegions(line, parsedState.item_count);
    const lineItem = document.getElementById(`CartDrawerItem-${line}`);
    if (lineItem && lineItem?.querySelector(`[name="${name}"]`))
      lineItem?.querySelector(`[name="${name}"]`).focus();
    this.disableLoading();

    if (!isDrawerOpened) {
      if (this.header) this.header.reveal();
      this.open();
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  updateLiveRegions(line, itemCount) {
    if (line == '' || !line || line == null) {
      return;
    }

    if (this.currentItemCount === itemCount) {
      const itemErrorHolder = document.getElementById(`CartDrawerItem-error-${line}`);
      if (itemErrorHolder) {
        const itemError = itemErrorHolder.querySelector('.cart-item__error-text');

        if (itemError) {
          itemErrorHolder.querySelector('.cart-item__error-text').innerHTML =
            window.cartStrings.quantityError.replace(
              '[quantity]',
              document.getElementById(`CartDrawerItem-quantity-${line}`).value
            );
        }
      }

      document.getElementById(`CartDrawerItem-${line}`)
        .querySelector('quantity-input')
        .classList
        .add('quantity--error');
    }

    this.currentItemCount = itemCount;
    this.lineItemStatusElement?.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-drawer-live-region-text');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  enableLoading(line) {
    document
      .getElementById('cart-drawer')
      .classList.add('cart-drawer--disabled');
    [...this.querySelectorAll(`#CartDrawerItem-${line} .loading-overlay`)].forEach(
      (overlay) => overlay.classList.remove('hidden')
    );
    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    document
      .getElementById('cart-drawer')
      .classList.remove('cart-drawer--disabled');
  }

  handleBodyClick(event) {
    const target = event.target;
    const isNotDrawer =
      target !== this.drawer && !target.closest('cart-drawer');
    const isNotButtonOpen =
      target !== this.cartDrawerOpen &&
      !target.closest('[data-js-cart-drawer="cartDrawerOpen"]');
    const isCloseButton =
      target === this.cartDrawerClose ||
      target.closest('[data-js-cart-drawer="cartDrawerClose"]');
    const isNotFreeSampleForm = !target.closest('.free-sample-product-form');

    if (
      (isNotDrawer && isNotButtonOpen && isNotFreeSampleForm) ||
      isCloseButton
    ) {
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  updateTrappedFocus() {
    removeTrapFocus(document.activeElement);

    setTimeout(() => {
      this.setActiveElement(this.cartDrawerClose || this.cartDrawerOpen);
      this.drawer.focus();
      trapFocus(this.drawer, this.cartDrawerClose);
    });
  }
}

customElements.define('cart-drawer', CartDrawer);
